// hooks/useInstallerLoanProducts.ts
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getRates } from '../services/apis/InstallerAPIs';
import {
  AssetType,
  ProductFamily,
  ProductModel,
  LoanTypeMapping,
} from '../../../../types/api';
import { getLoanYearsOptions } from '../utils/helpers';
import { Choice } from '../../../../types/utility';
import { Fees } from '../modules/loan/fees';
import { ProductFamilies } from '../modules/loan/productFamilies';

export const useInstallerLoanProducts = ({
  loanType,
  loanTerm,
  selectedProductId,
  loanPricingDate,
  selectedAssetTypes,
}: {
  loanType?: ProductFamily;
  loanTerm?: string;
  selectedProductId?: string;
  loanPricingDate?: string;
  selectedAssetTypes?: ReadonlyArray<AssetType>;
}) => {
  const { data } = useQuery(['rates', loanPricingDate], () =>
    getRates(loanPricingDate)
  );

  const currentRates = useMemo(() => {
    if (!data) return [];
    const { current } = data;
    const rates = [
      ...(current[ProductFamily.STANDARD] ?? []),
      ...(current[ProductFamily.SPECIAL_PAYMENT] ?? []),
      ...(current[ProductFamily.TAX_CREDIT] ?? []),
      ...(current[ProductFamily.FLEX] ?? []),
    ];
    return rates.filter((rate) =>
      Fees.isVisibleForSelectedAssets(
        rate.applies_to_assets,
        rate.exclude_for_assets,
        selectedAssetTypes ?? []
      )
    );
  }, [data, selectedAssetTypes]);

  const productList: ProductModel[] = useMemo(() => {
    return currentRates.map((rate) => ({
      id: rate.product || '',
      amortizationYears: parseInt(rate.amortization_years ?? '0'),
      dealerFee: rate.dealer_fee,
      loanTermYears: parseInt(rate.loan_term_years),
      productFamily: rate.product_family,
      rateId: rate.id,
      rate: rate.interest_rate,
      rateScenario: rate.pricing_grouping,
      type: 'current',
    }));
  }, [currentRates]);

  const allowedFamilies = useMemo(
    () => ProductFamilies.forAssetTypes(selectedAssetTypes),
    [selectedAssetTypes]
  );

  const productFamilyOptions: Choice[] = useMemo(() => {
    return Array.from(
      new Set(
        productList
          .map(({ productFamily }) => productFamily)
          .filter((family) => allowedFamilies.includes(family))
      )
    ).map((productFamily) => ({
      label: LoanTypeMapping[productFamily] ?? '',
      value: productFamily,
    }));
  }, [productList, allowedFamilies]);

  const loanYearsOptions = useMemo(() => {
    return getLoanYearsOptions(productList, loanType, true);
  }, [loanType, productList]);

  const interestRateOptions: Choice[] = useMemo(() => {
    const currentTerm = loanTerm ? parseInt(loanTerm.slice(0, 2)) : -1;
    const currentAmort =
      loanTerm?.slice(-2) === '00'
        ? null
        : parseInt(loanTerm?.slice(-2) || '-1');

    return productList
      .filter((item) => item && item.productFamily)
      .filter(({ productFamily }) =>
        loanType === ProductFamily.STANDARD
          ? [ProductFamily.STANDARD, ProductFamily.PREMIUM].includes(
              productFamily
            )
          : productFamily === loanType
      )
      .filter(
        (item) =>
          item.loanTermYears !== undefined &&
          item.amortizationYears !== undefined
      )
      .filter(
        ({ loanTermYears, amortizationYears }) =>
          loanTermYears === currentTerm && amortizationYears === currentAmort
      )
      .sort((a, b) => Number(a.rate) - Number(b.rate))
      .map(({ id, rate }) => ({
        value: id,
        label: `${rate}%`,
      }));
  }, [loanType, loanTerm, productList]);

  // A product id left over from another type or term is not a selected rate.
  const offeredProduct = useMemo(() => {
    const isOffered = interestRateOptions.some(
      (option) => option.value === selectedProductId
    );
    if (!isOffered) return undefined;
    return productList.find((product) => product.id === selectedProductId);
  }, [interestRateOptions, productList, selectedProductId]);

  return {
    productList,
    productFamilyOptions,
    loanYearsOptions,
    interestRateOptions,
    selectedProductIsPremium:
      offeredProduct?.productFamily === ProductFamily.PREMIUM,
    selectedInterestRate: offeredProduct?.rate ?? null,
    dealerFee: offeredProduct?.dealerFee ?? 0,
    dealerFeeId: offeredProduct?.rateId ?? null,
    ratesLoaded: Boolean(data),
  };
};
